import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Award, 
  Edit, 
  Save, 
  Plus, 
  Calendar, 
  Trash2 
} from 'lucide-react';
import { buildApiUrl, getApiHeaders } from '@/config/api';

const toast = {
  success: (message: string) => {
    console.log('✅ Success:', message);
    const toastEl = document.createElement('div');
    toastEl.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center gap-2';
    toastEl.innerHTML = `
      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
      </svg>
      ${message}
    `;
    document.body.appendChild(toastEl);
    setTimeout(() => {
      toastEl.remove();
    }, 4000);
  },
  error: (message: string) => {
    console.error('❌ Error:', message);
    const toastEl = document.createElement('div');
    toastEl.className = 'fixed top-4 right-4 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center gap-2';
    toastEl.innerHTML = `
      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
      </svg>
      ${message}
    `;
    document.body.appendChild(toastEl);
    setTimeout(() => {
      toastEl.remove();
    }, 5000);
  }
};

interface AwardsTabProps {
  data: any;
  loading: boolean;
  isEditable: boolean;
  editMode: boolean;
  onEdit: () => void;
  onSave: (data: any) => void;
  onCancel: () => void;
  onRefresh?: () => void;
}

const AwardsTab: React.FC<AwardsTabProps> = ({ 
  data, 
  loading, 
  isEditable, 
  editMode, 
  onEdit, 
  onSave, 
  onCancel,
  onRefresh 
}) => {
  const [editData, setEditData] = useState(data);
  const updateEditData = (updater: (prev: any) => any) => setEditData((prev: any) => updater(prev));
  const [showAddForm, setShowAddForm] = useState(false);
  const [newAward, setNewAward] = useState({
    title: '',
    issuing_organization: '',
    award_date: '',
    description: '',
    type: 'award'
  });

  useEffect(() => {
    setEditData(data);
  }, [data]);

  if (loading) {
    return <Card><CardContent className="p-6">Loading awards and achievements...</CardContent></Card>;
  }

  const handleSave = () => {
    onSave(editData);
  };

  const handleCancel = () => {
    setEditData(data);
    onCancel();
  };

  const handleAwardChange = (index: number, field: string, value: any) => {
    updateEditData((prev: any) => ({
      ...prev,
      awards_achievements: prev?.awards_achievements?.map((award: any, i: number) => 
        i === index ? { ...award, [field]: value } : award
      ) || []
    }));
  };

  const handleAddNewAward = async () => {
    if (!newAward.title.trim()) {
      toast.error('Award title is required');
      return;
    }
    
    if (!newAward.issuing_organization.trim()) {
      toast.error('Issuing organization is required');
      return;
    }
    
    if (!newAward.award_date.trim()) {
      toast.error('Award date is required');
      return;
    }

    try {
      const url = buildApiUrl('awards/me/awards');
      const headers = getApiHeaders(true);
      
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(newAward)
      });

      if (!response.ok) {
        let errorMessage = 'Failed to add award';
        try {
          const errorData = await response.json();
          if (response.status === 401) {
            errorMessage = 'Authentication required. Please log in again.';
          } else if (response.status === 403) {
            errorMessage = 'Access denied. You do not have permission to add awards.';
          } else if (response.status === 422) {
            if (errorData.detail && Array.isArray(errorData.detail)) {
              errorMessage = errorData.detail.map((err: any) => 
                typeof err === 'string' ? err : 
                err.msg || err.message || JSON.stringify(err)
              ).join(', ');
            } else {
              errorMessage = errorData.detail || errorData.message || JSON.stringify(errorData);
            }
          } else {
            errorMessage = errorData.detail || errorData.message || JSON.stringify(errorData);
          }
        } catch (e) {
          errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      
      if (result.success) {
        toast.success('Award added successfully');
        setNewAward({
          title: '',
          issuing_organization: '',
          award_date: '',
          description: '',
          type: 'award'
        });
        setShowAddForm(false);
        if (onRefresh) {
          onRefresh();
        }
      } else {
        throw new Error(result.message || 'Failed to add award');
      }
    } catch (error: any) {
      console.error('❌ Error adding award:', error);
      
      let errorMessage = 'Unknown error occurred';
      
      if (error?.message) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      } else if (error?.toString && typeof error.toString === 'function') {
        try {
          errorMessage = error.toString();
        } catch (e) {
          errorMessage = 'Error converting error to string';
        }
      } else {
        try {
          errorMessage = JSON.stringify(error);
        } catch (e) {
          errorMessage = 'Error serializing error object';
        }
      }
      
      toast.error(`Error adding award: ${errorMessage}`);
    }
  };

  const handleDeleteAward = async (awardId: number, awardTitle: string) => {
    console.log('🔍 handleDeleteAward called with:', { awardId, awardTitle });
    
    if (!awardId || awardId === undefined || awardId === null) {
      toast.error('Cannot delete award: Invalid award ID');
      return;
    }
    
    if (!window.confirm(`Are you sure you want to delete the award "${awardTitle}"?`)) {
      return;
    }

    try {
      const url = buildApiUrl(`awards/me/awards/${awardId}`);
      const headers = getApiHeaders(true);
      
      const response = await fetch(url, {
        method: 'DELETE',
        headers
      });

      if (!response.ok) {
        let errorMessage = 'Failed to delete award';
        try {
          const errorData = await response.json();
          if (response.status === 401) {
            errorMessage = 'Authentication required. Please log in again.';
          } else if (response.status === 403) {
            errorMessage = 'Access denied. You do not have permission to delete awards.';
          } else if (response.status === 422) {
            if (errorData.detail && Array.isArray(errorData.detail)) {
              errorMessage = errorData.detail.map((err: any) => 
                typeof err === 'string' ? err : 
                err.msg || err.message || JSON.stringify(err)
              ).join(', ');
            } else {
              errorMessage = errorData.detail || errorData.message || JSON.stringify(errorData);
            }
          } else {
            errorMessage = errorData.detail || errorData.message || JSON.stringify(errorData);
          }
        } catch (e) {
          errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      
      if (result.success) {
        toast.success(`Award "${awardTitle}" deleted successfully`);
        if (onRefresh) {
          onRefresh();
        }
      } else {
        throw new Error(result.message || 'Failed to delete award');
      }
    } catch (error: any) {
      console.error('❌ Error deleting award:', error);
      
      let errorMessage = 'Unknown error occurred';
      
      if (error?.message) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      } else if (error?.toString && typeof error.toString === 'function') {
        try {
          errorMessage = error.toString();
        } catch (e) {
          errorMessage = 'Error converting error to string';
        }
      } else {
        try {
          errorMessage = JSON.stringify(error);
        } catch (e) {
          errorMessage = 'Error serializing error object';
        }
      }
      
      toast.error(`Error deleting award: ${errorMessage}`);
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <Award className="h-5 w-5" />
          Awards & Achievements ({data?.total_count || 0})
        </CardTitle>
        <div className="flex gap-2">
          {isEditable && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowAddForm(true)}
              className="bg-green-50 hover:bg-green-100 border-green-200"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Award
            </Button>
          )}
          {isEditable && !editMode && (
            <Button variant="outline" size="sm" onClick={onEdit}>
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {data?.awards_achievements?.length > 0 ? (
          <div className="space-y-4">
            {(editMode ? editData?.awards_achievements : data.awards_achievements)?.map((award: any, index: number) => {
              console.log('🔍 Award item:', { id: award.id, uuid: award.uuid, awardTitle: award.awardTitle, issuer: award.issuer });
              return (
              <div key={award.uuid || index} className="border rounded-lg p-4">
                <div className="flex justify-between items-start mb-2">
                  {editMode ? (
                    <input
                      type="text"
                      value={award.awardTitle || ''}
                      onChange={(e) => handleAwardChange(index, 'awardTitle', e.target.value)}
                      className="font-semibold text-lg border-b-2 border-gray-300 focus:border-blue-500 outline-none bg-transparent"
                      placeholder="Award Title"
                    />
                  ) : (
                    <h3 className="font-semibold">{award.awardTitle}</h3>
                  )}
                  <div className="flex items-center gap-2">
                    <Badge variant={award.associatedWith === 'award' ? "default" : "secondary"}>
                      {award.associatedWith}
                    </Badge>
                    {editMode && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteAward(award.id, award.awardTitle)}
                        className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50 ml-2"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
                {editMode ? (
                  <input
                    type="text"
                    value={award.issuer || ''}
                    onChange={(e) => handleAwardChange(index, 'issuer', e.target.value)}
                    className="text-sm text-gray-600 mb-2 w-full border-b border-gray-300 focus:border-blue-500 outline-none bg-transparent"
                    placeholder="Issuing Organization"
                  />
                ) : (
                  <p className="text-sm text-gray-600 mb-2">{award.issuer}</p>
                )}
                {editMode ? (
                  <textarea
                    value={award.description || ''}
                    onChange={(e) => handleAwardChange(index, 'description', e.target.value)}
                    rows={2}
                    className="text-sm w-full border border-gray-300 rounded-md px-3 py-2 focus:border-blue-500 outline-none"
                    placeholder="Award description"
                  />
                ) : (
                  award.description && <p className="text-sm mb-2">{award.description}</p>
                )}
                <div className="flex items-center gap-2 mt-2 text-sm text-gray-500">
                  <Calendar className="h-4 w-4" />
                  {editMode ? (
                    <input
                      type="date"
                      value={award.awardDate || ''}
                      onChange={(e) => handleAwardChange(index, 'awardDate', e.target.value)}
                      className="border rounded px-2 py-1"
                    />
                  ) : (
                    <span>{award.awardDate}</span>
                  )}
                </div>
              </div>
              );
            })}
          </div>
        ) : (
          <p className="text-center text-gray-500">No awards or achievements available</p>
        )}

        {showAddForm && (
          <div className="border rounded-lg p-4 mt-4 bg-gray-50">
            <h4 className="font-semibold mb-4 flex items-center gap-2">
              <Plus className="h-4 w-4" />
              Add New Award
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Award Title *
                </label>
                <input
                  type="text"
                  value={newAward.title}
                  onChange={(e) => setNewAward(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                  placeholder="e.g., Best Developer Award"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type
                </label>
                <select
                  value={newAward.type}
                  onChange={(e) => setNewAward(prev => ({ ...prev, type: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                >
                  <option value="award">Award</option>
                  <option value="achievement">Achievement</option>
                  <option value="recognition">Recognition</option>
                  <option value="honor">Honor</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Issuing Organization *
                </label>
                <input
                  type="text"
                  value={newAward.issuing_organization}
                  onChange={(e) => setNewAward(prev => ({ ...prev, issuing_organization: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                  placeholder="e.g., Tech Company Inc."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Award Date *
                </label>
                <input
                  type="date"
                  value={newAward.award_date}
                  onChange={(e) => setNewAward(prev => ({ ...prev, award_date: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={newAward.description}
                  onChange={(e) => setNewAward(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                  placeholder="Brief description of the award..."
                />
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <Button onClick={handleAddNewAward} className="bg-green-600 hover:bg-green-700">
                <Save className="h-4 w-4 mr-2" />
                Add Award
              </Button>
              <Button variant="outline" onClick={() => setShowAddForm(false)}>
                Cancel
              </Button>
            </div>
          </div>
        )}
        
        {editMode && isEditable && (
          <div className="flex gap-2 pt-4">
            <Button onClick={handleSave}>
              <Save className="h-4 w-4 mr-2" />
              Save Changes
            </Button>
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AwardsTab;
